/**
 * This is a very simple holiday checker (almost quick&dirty).
 * As libraries like date-holiday offer more functionality than needed
 * for this project with a high footprint.
 * Holidays are provided in simple JSON.
 */

// All available countries
const supportedCountries = ['de', 'fr', 'es', 'it', 'gb', 'au', 'nl', 'uk']

// Type reflecting data from local json files
type Holiday = {
  date: string
  name: string
}

// Cache
const holidaysCache = {}

/**
 * Load the holidays JSON for a given country
 * @param {string} countryCode e.g. 'de', 'fr'
 * @returns {Promise<Array<{date:string, name:string}>>}
 */
export async function loadHolidays(countryCode): Promise<Holiday[]> {
  countryCode = countryCode.toLowerCase()
  if (!supportedCountries.includes(countryCode)) {
    return []
  }
  if (holidaysCache[countryCode]) {
    return holidaysCache[countryCode]
  }
  const res = await fetch(`/holidays/${countryCode}.json`)
  if (!res.ok) {
    throw new Error(`Failed to load holidays for ${countryCode}`)
  }
  const data = await res.json()
  holidaysCache[countryCode] = data
  return data
}

type HolidayInfo = {
  isHoliday: boolean
  holidayName?: string
  date: Date
  nextWorkingDay?: Date
}

/**
 * Check if a date is a holiday. Additionally, if searchNextWorkingDate is true, it also gives the next working day.
 * @param {Date} theDate
 * @param {string} countryCode
 * @param {boolean} searchNextWorkingDate set to true to also search for the next working day
 * @returns {Promise<{isHoliday: boolean, holidayName?: string}>}
 */
export async function isPublicHoliday(
  theDate: Date,
  countryCode: string,
  searchNextWorkingDate: boolean
): Promise<HolidayInfo> {
  const holidays = await loadHolidays(countryCode)
  const holiday = isHoliday(theDate, holidays)
  if (holiday) {
    // We also search for the next working day if needed
    if (searchNextWorkingDate) {
      const nextDate = new Date(theDate)
      nextDate.setDate(nextDate.getDate() + 1)
      while (isWeekend(nextDate) || isHoliday(nextDate, holidays)) {
        nextDate.setDate(nextDate.getDate() + 1)
      }
      return { isHoliday: true, holidayName: holiday.name, date: theDate, nextWorkingDay: nextDate }
    }
    return { isHoliday: true, holidayName: holiday.name, date: theDate }
  }
  return { isHoliday: false, date: theDate }
}

/** Find one holiday from the holidays JSON using a specific date format */
const isHoliday = (date: Date, holidays: Holiday[]) => holidays.find((h) => h.date === dateToStringYYYMMDD(date))

/**
 * Return "YYYY-MM-DD" same format as in JSON
 * @param date
 */
function dateToStringYYYMMDD(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-based
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function isWeekend(date: Date) {
  const day = date.getDay()
  return day === 0 || day === 6 // Sunday = 0, Saturday = 6
}
